import {
  BaseConfirmationType,
  type ActivityPlan,
  type DatetimeDate,
  type Product
} from '@linktivity/ars-neo';
import { OPTION_UNIT_RESTRICTION_STATUS } from '@link/share/constants';
import type { PlanState } from '@link/share/hooks/planState';
import type { OptionUnitRestrictionResult } from '@link/share/utils';
import type { DayUnit } from './calendarRange';

/*
 * Whether the booking can be submitted, and if not, why. From share's
 * `useProductPage` (`orderDisabledBase`), which folded the same checks into
 * one boolean; listing the reasons keeps each check visible on its own.
 */

export type LotteryPhase = 'before' | 'open' | 'after';

const toDateKey = (date?: DatetimeDate): number | undefined => {
  if (!date?.year || !date.month || !date.day) return undefined;
  return date.year * 10000 + date.month * 100 + date.day;
};

/**
 * Where today falls in a lottery's application period; undefined for a
 * product that is not a lottery or has no period. Local date, as in the SPA.
 */
export function lotteryPhase(
  product: Product,
  today: Date
): LotteryPhase | undefined {
  if (
    product.confirmationType !== BaseConfirmationType.ConfirmationTypeLottery
  ) {
    return undefined;
  }
  const start = toDateKey(product.bookingOpenPeriod?.start);
  const end = toDateKey(product.bookingOpenPeriod?.end);
  if (start === undefined || end === undefined) return undefined;
  const todayKey =
    today.getFullYear() * 10000 +
    (today.getMonth() + 1) * 100 +
    today.getDate();
  if (todayKey < start) return 'before';
  if (todayKey >= end) return 'after';
  return 'open';
}

/** The submit button's label, which a lottery words by its phase. */
export function submitLabelKey(
  isLottery: boolean,
  phase: LotteryPhase | undefined
): string {
  if (!isLottery) return 'share.payment.submit';
  if (phase === 'before') return 'share.payment.lotteryBeforeOpen';
  if (phase === 'after') return 'share.payment.lotteryClosed';
  return 'share.payment.lotterySubmit';
}

export type OrderBlocker =
  | 'noPlan'
  | 'noDate'
  | 'noTime'
  | 'noUnits'
  | 'belowMinCapacity'
  | 'aboveMaxCapacity'
  | 'dependentUnitAlone'
  | 'lotteryClosed'
  | 'optionMissing'
  | 'optionUnitRestriction'
  | 'optionsSoldOutOnDate';

export interface OrderGateInput {
  activePlan: ActivityPlan | undefined;
  activePlanState: PlanState | undefined;
  /** The units the active plan sells on the selected day. */
  dayUnits: DayUnit[];
  /**
   * The most capacity-counting units the selected slot takes: the plan's
   * maximum, lowered to what is left. Undefined for no limit.
   */
  maxCapacity: number | undefined;
  /** A unit that cannot be booked alone is selected alone. */
  dependentUnitWarning: boolean;
  lotteryPhase: LotteryPhase | undefined;
  /** Options are required and none is selected. */
  missingRequiredOption: boolean;
  optionUnitRestriction: OptionUnitRestrictionResult;
  optionSoldOutDates: Set<string>;
}

/** Everything standing between the visitor and submitting; empty when none. */
export function orderBlockers(input: OrderGateInput): OrderBlocker[] {
  const { activePlan, activePlanState: state } = input;
  if (!activePlan || !state) return ['noPlan'];
  const blockers: OrderBlocker[] = [];
  if (!state.selectedDate) blockers.push('noDate');
  if (!state.selectedTimeId) blockers.push('noTime');
  if (!state.selectedUnits.some(unit => (unit.quantity ?? 0) > 0)) {
    blockers.push('noUnits');
  }
  if (isBelowMinCapacity(activePlan, state, input.dayUnits)) {
    blockers.push('belowMinCapacity');
  }
  // Quantities survive a date or time change, so a count picked against one
  // slot can exceed what another has left.
  if (
    input.maxCapacity !== undefined &&
    countedCapacity(state, input.dayUnits) > input.maxCapacity
  ) {
    blockers.push('aboveMaxCapacity');
  }
  if (input.dependentUnitWarning) blockers.push('dependentUnitAlone');
  if (input.lotteryPhase === 'before' || input.lotteryPhase === 'after') {
    blockers.push('lotteryClosed');
  }
  if (input.missingRequiredOption) blockers.push('optionMissing');
  if (
    input.optionUnitRestriction.status !== OPTION_UNIT_RESTRICTION_STATUS.VALID
  ) {
    blockers.push('optionUnitRestriction');
  }
  // The moment between the options' calendars arriving and the selected
  // date being moved off a day they sold out.
  if (state.selectedDate && input.optionSoldOutDates.has(state.selectedDate)) {
    blockers.push('optionsSoldOutOnDate');
  }
  return blockers;
}

/**
 * How many capacity-counting units are selected. Counts only the capacity
 * units the selected day sells (the selection can keep units from another
 * day).
 */
function countedCapacity(state: PlanState, dayUnits: DayUnit[]): number {
  const capacityUnitIds = new Set(
    dayUnits.filter(unit => unit.includeCapacity).map(unit => unit.id)
  );
  return state.selectedUnits.reduce(
    (sum, unit) =>
      sum + (capacityUnitIds.has(unit.id) ? (unit.quantity ?? 0) : 0),
    0
  );
}

/**
 * Fewer capacity-counting units than the plan's minimum. A product with no
 * capacity units at all is misconfigured and is not blocked for it.
 */
function isBelowMinCapacity(
  plan: ActivityPlan,
  state: PlanState,
  dayUnits: DayUnit[]
): boolean {
  const min = plan.minCapacityInclusive;
  if (!min || !dayUnits.some(unit => unit.includeCapacity)) return false;
  return countedCapacity(state, dayUnits) < min;
}
